import { create } from 'zustand';
import { logger } from '../helpers/logger';
import { buildWhatsappMessage, buildWhatsappMessageParams } from './helpers/buildWhatsappMessage';
import {
  Prescription,
  PrescriptionDocument,
  PRESCRIPTION_STATUS,
  ACTIVE_TAB,
  ACTIVE_TAB_MAP_TO_SEARCH_TAB,
} from '../interfaces/prescription';
import {
  Benefit,
  OwnedCertificate,
  PDFModelLayout,
  PreLoadingData,
  PrescriberConfiguration,
  PrescriberConfigurations,
  PrescriberService,
} from '../shared/prescriber';
import { ManualMedicine, Medicine } from '../interfaces/medicine';
import { MedicineService } from '../components/search/medicineService';
import { Tracker } from '../shared/tracker/Tracker';
import {
  PrescriptionService,
  TokenExpirationData,
  UpdatePrescriptionPayload,
} from '../containers/prescription/service';
import { toast } from '../helpers/toast';
import { MedicalReport, MedicalReportDTO } from '../interfaces/medicalReport';
import { MedicalReportService } from '../containers/prescription/medicalReportService';
import { MedicalReferral, MedicalReferralDTO } from '../interfaces/MedicalReferral';
import { Exam, FavoriteExam } from '../interfaces/exam';
import { ExamService } from '../components/search/examService';
import { MedicalCertificate, MedicalCertificateDTO } from '../interfaces/medicalCertificate';
import { MedicalCertificateService } from '../containers/medical-certificate/medicalCertificateService';
import { Prescriber, RQE } from '../interfaces/users';
import { isEmpty, get as LodashGet, pick } from 'lodash';
import { useInteractionStore } from './interactionStore';
import {
  safeDeleteFromLocalStorage,
  safeGetFromLocalStorage,
  safeSetInLocalStorage,
  sendCancelPostMessage,
  sendExcludedPostMessage,
  sendWhatsappPostMessage,
} from '../helpers/window';
import { CertificateService } from '../containers/certificate/certificateService';
import {
  Certificate,
  certificates,
  CERTIFICATES,
  CERTIFIER_STATUS,
  isCertifierUnstable,
} from '../containers/certificate/certificates';
import { authenticateSafeweb, authenticateSoluti } from '../containers/certificate/AuthenticateCloudCertificate';
import { CertificateFromLacuna, LacunaService } from '../containers/certificate/lacunaService';
import { sleep } from '../helpers/sleep';
import { setSurvicate } from '../helpers/survicate';
import Cookies from 'js-cookie';
import {
  clearPrescriptionStartedPayload,
  clearPrescriptionCompletedPayload,
  clearConcomitanceMap,
  clearSuggestedPosologyMap,
  clearProtocolMap,
  buildPrescriptionCompletedPayload,
  buildPrescriptionSentPayload,
  getPrescriptionCompletedPayload,
  amplitudeModule,
} from '../shared/amplitude';
import { useExamStore } from './examStore';
import {
  DEFAULT_PRIMARY_COLOR,
  DEFAULT_SECONDARY_COLOR,
} from '../containers/prescription/usePrescription/helpers.mock';
import { colorHandler } from '../helpers/colorHandler';
import {
  adaptMedicineToAddPrescriptionParam,
  adaptMedicineToEditPrescriptionParam,
} from '../providers/PrescriptionProvider';
import { MedicineCategorizer } from '../helpers/MedicineCategorizer';
import { useSearchTypeStore } from './searchTypeStore';
import { SEARCH_TYPE } from '../components/search/SearchTypeEnum';
import { onlyDigits } from '../helpers/string';
import manualLogo from '../assets/images/certificates/manual.png';

interface GetAndSetPrescriberCertificatesParams {
  prescriberDocument: string;
  prescriptionId: number;
  lastSignatureMethod: CERTIFICATES;
  isDigitalCertificateRequired: boolean;
}

interface PrescriberCoreDataParams extends GetAndSetPrescriberCertificatesParams {
  prescriberId: number;
}

export interface ClosePrescriptionPayload {
  email: string;
  phone: string;
  generatePdf: boolean;
  openPatientApp: boolean;
  certificateType: CERTIFICATES | null;
  certificateAuthentication: Record<string, any> | null;
  localCertificate: CertificateFromLacuna | null;
  patientData?: PatientData;
  prescriptionObservation: string;
  shouldSendPrescriptionWhatsApp: boolean;
  showDateInPdf: boolean;
  emittedTo: Date | null;
  establishment?: {
    address1: string;
    address2: string;
    neighborhood: string;
    city: string;
    state: string;
    postalCode: string;
  };
  issueMedicinesInSeparateTabs: boolean;
  hasDigitalCertificates: boolean;
  skipDefaultRqe?: boolean;
}

export interface ClosePrescriptionResponse {
  closed: boolean;
  errorMessage: string;
  hasCertificateError?: boolean;
}

interface PatientData {
  address?: {
    address: string;
    city: string;
    federalUnit: string;
    number: string;
    neighborhood: string;
    postalCode: string;
    complement: string;
  };
  gender?: string;
  birthDate?: string;
  icd10structured?: {
    code: string;
    idICD10: number;
    description: string;
  }[];
  document?: string;
  passport?: {
    number: string;
    country: string;
    emissionCountry: string;
  };
}

export enum TEST_FLOW {
  A = 'A',
  B = 'B',
  C = 'C',
}

export interface PrescriptionStore {
  prescription: Prescription | null;
  setPrescription: (prescription: Prescription) => void;
  preLoadingData: PreLoadingData | null;
  setPreLoadingData: (preLoadingData: PreLoadingData | null) => void;
  showedHighlightedModels: boolean | null;
  setShowedHighlightedModels: (showedHighlightedModels: boolean | null) => void;
  prescriberConfiguration: PrescriberConfiguration | null;
  setPrescriberConfiguration: (prescriberConfiguration: PrescriberConfiguration | null) => void;

  benefits: Benefit[];
  setBenefits: (benefits: Benefit[]) => void;
  benefit: () => Benefit | null;

  addMedicineToPrescription: (params: { medicine: Medicine; searchMedicineId: number | null }) => Promise<void>;

  prescriber: () => null | Prescriber;

  validationCode: () => string;
  shouldBypassCertificateFlow: boolean;
  getAndSetBypass: () => Promise<void>;

  showByActivePrinciple: () => boolean;
  setShowByActivePrinciple(prescriptionId: number, showByActivePrinciple: boolean): Promise<void>;

  addManualMedicineToPrescription(manualMedicine: ManualMedicine): Promise<void>;
  removeMedicineFromPrescription(medicine: Medicine): Promise<void>;
  updatePrescriptionMedicine(medicine: Medicine): Promise<void>;
  updatePrescriptionManualMedicine(manualMedicine: ManualMedicine): Promise<void>;
  removePrescriptionManualMedicine(manualMedicine: ManualMedicine): Promise<void>;
  addExamToPrescription(params: { exam: Exam; searchExamLogId: number | null }): Promise<void>;
  removeExamFromPrescription(exam: Exam): Promise<void>;
  updatePrescriptionExam(exam: Exam): Promise<void>;
  addMedicalCertificateToPrescription(data: MedicalCertificateDTO): Promise<void>;
  removeMedicalCertificateFromPrescription(prescriptionMedicalCertificateId: number): Promise<void>;
  updatePrescriptionMedicalCertificate(data: MedicalCertificateDTO): Promise<void>;
  addMedicalReferralToPrescription(medicalReferral: MedicalReferral): Promise<void>;
  updateMedicalReferral(medicalReferral: MedicalReferral): Promise<void>;
  removeMedicalReferralFromPrescription(medicalReferralId: number): Promise<void>;
  removeMedicalReportFromPrescription(prescriptionMedicalReportId: number): Promise<void>;
  updatePrescriptionMedicalReport(medicalReport: MedicalReportDTO): Promise<void>;
  addMedicalReportToPrescription(medicalReport: MedicalReportDTO): Promise<void>;
  prescriptionObservation: () => string;
  updatePrescriptionObservation(observation: string | null): void;
  onUpdatePrescriptionObservation(observation: string | null): Promise<void>;
  prescriptionItemsAmount: () => number;
  addItemsFromPrescription(
    currentPrescriptionId: number,
    originPrescriptionId: number,
    removeItems: boolean,
    addLmeFromOriginPrescription: boolean
  ): Promise<void>;
  partnerJustifyInteraction: () => boolean;
  enableTussCodeOnInterface: () => boolean;

  simplePrescriptionLayouts: PDFModelLayout[];
  setSimplePrescriptionLayouts(layouts: PDFModelLayout[]): void;
  specialPrescriptionLayouts: PDFModelLayout[];
  setSpecialPrescriptionLayouts(layouts: PDFModelLayout[]): void;
  digitalCertificateIsRequired: () => boolean;
  updatePatientAndIcdData(layouts: PDFModelLayout[]): Promise<void>;

  isPrescriptionClosed: boolean;
  setIsPrescriptionClosed(value: boolean): void;

  getAndSetPrescriptionByReference(referenceCode: string): Promise<Prescription | null>;

  prescriptionTokenExpirationData: TokenExpirationData | null;
  setPrescriptionTokenExpirationData(value: TokenExpirationData | null): void;
  getAndSetPrescriberConfigurations(referenceCode: string): Promise<void>;
  fetchPrescriptionByReference(referenceCode: string | null): Promise<Prescription | null>;
  logoUrl: () => string | null;
  postClosePrescriptionMessage(updatedPrescription: Prescription): void;
  openPrescriptionDocuments(documents: PrescriptionDocument[]): void;
  openPatientAppWindow(patientAppUrl: string, validationCode: string): void;
  openWhatsAppToSendPrescription(prescription: Prescription, phoneNumber: string, accessCode: string): void;
  finishPrescriptionEmission(
    generatePdf: boolean,
    openPatientApp: boolean,
    shouldSendPrescriptionWhatsApp: boolean,
    phoneNumber: string
  ): Promise<boolean>;

  prescriptionEmissionFallBackOnTimeout(
    generatePdf: boolean,
    openPatientApp: boolean,
    shouldSendPrescriptionWhatsApp: boolean,
    phoneNumber: string
  ): Promise<boolean>;
  closePrescriptionWithLocalCertificate(payload: ClosePrescriptionPayload): Promise<boolean>;
  closePrescription(payload: ClosePrescriptionPayload): Promise<ClosePrescriptionResponse>;
  cancelOrContinueLater(confirmation: boolean, reason?: string): Promise<void>;
  updatePatientAndIcdData(): void;
  fetchPdfLayoutModels(): Promise<void>;
  primaryColor: () => string;
  secondaryColor: () => string;

  getAndSetPrescriberCertificates(params: GetAndSetPrescriberCertificatesParams): Promise<void>;

  ownedCertificates: OwnedCertificate[];
  setOwnedCertificates(certificates: OwnedCertificate[]): void;

  selectedCertificateType: CERTIFICATES | null;
  setSelectedCertificateType(certificates: CERTIFICATES | null): void;
  filteredCertificates: () => Certificate[];
  getPrescriberCoreData(params: PrescriberCoreDataParams): Promise<void>;
  setPrescriberConfigurations(data: PrescriberConfigurations | null): void;
  getAndSetPrescriberConfiguration(prescriberId: number): Promise<void>;
  setPrescriberConfigurations(data: PrescriberConfigurations | null): void;
  prescriberConfigurations: PrescriberConfigurations | null;
  rqes: RQE[];
  setRqes(data: RQE[]): void;
  selectedRqe: number | null;
  setSelectedRqe(data: number | null): void;
  selectedLocalCertificate: CertificateFromLacuna | null;
  setSelectedLocalCertificate(data: CertificateFromLacuna | null): void;

  searchMedicineId: number;
  setSearchMedicineId(data: number): void;
  doesPrescriptionHasSigningWarning: () => boolean;
  initialActiveTab: SEARCH_TYPE;
  setInitialActiveTab: (tab?: ACTIVE_TAB) => void;
  hideManualSigningMethod: boolean;
  setHideManualSigningMethod: (value: boolean) => void;

  emittedTo: null | Date;
  setEmittedTo: (value: Date) => void;

  shouldHideCertificates: boolean;
  setShouldHideCertificates: (value: boolean) => void;
  userCertificates: () => Certificate[];
  addFavoriteExam(exam: Exam): Promise<void>;
  removeFavoriteExam(exam: Exam): Promise<void>;
  favoriteExams: FavoriteExam[];
  setFavoriteExams: (favoriteExams: FavoriteExam[]) => void;
  fetchFavoriteExams(): Promise<void>;
  isBannerShown: boolean;
  setIsBannerShown: (data: boolean | null) => void;
  isFormulaFormDialogOpen: boolean;
  setIsFormulaFormDialogOpen: (value: boolean) => void;
  itemsAddedFromModel: boolean;
  setItemsAddedFromModel: (value: boolean) => void;
}

export const usePrescriptionStore = create<PrescriptionStore>()((set, get) => ({
  prescription: null,
  setPrescription: (prescription: Prescription | null) => {
    if (!prescription) return;
    set((prev) => {
      return {
        ...prev,
        prescription,
      };
    });
  },
  isFormulaFormDialogOpen: false,
  setIsFormulaFormDialogOpen: (value: boolean) => {
    set((prev) => {
      return {
        ...prev,
        isFormulaFormDialogOpen: value,
      };
    });
  },
  itemsAddedFromModel: false,
  setItemsAddedFromModel: (value: boolean) => {
    set((prev) => {
      return {
        ...prev,
        itemsAddedFromModel: value,
      };
    });
  },
  preLoadingData: null,
  setPreLoadingData: (preLoadingData) => {
    if (!preLoadingData) return;

    set((prev) => {
      return {
        ...prev,
        preLoadingData,
      };
    });
  },

  showedHighlightedModels: false,
  setShowedHighlightedModels: (showedHighlightedModels) => {
    if (!showedHighlightedModels) return;

    set((prev) => {
      return {
        ...prev,
        showedHighlightedModels,
      };
    });
  },
  isBannerShown: false,

  setIsBannerShown: (shouldShowBanner) => {
    if (shouldShowBanner === null) return;

    set((prev) => {
      return {
        ...prev,
        isBannerShown: shouldShowBanner,
      };
    });
  },
  prescriber: () => get().prescription?.Prescritor || null,
  validationCode: () => get().prescription?.CodigoValidacao || '',

  prescriberConfiguration: null,
  setPrescriberConfiguration: (prescriberConfiguration) => {
    if (!prescriberConfiguration) return;

    set((prev) => {
      return {
        ...prev,
        prescriberConfiguration,
      };
    });
  },

  benefits: [],
  setBenefits: (benefits) => {
    set((prev) => {
      return {
        ...prev,
        benefits,
      };
    });
  },

  benefit: () => LodashGet(get().benefits, '[0]', null),

  showByActivePrinciple: () => !!get().prescription?.ExibirPorPrincipioAtivo,

  setShowByActivePrinciple: async (prescriptionId: number, showByActivePrinciple: boolean) => {
    try {
      await PrescriptionService.updatePrescriptionShowByActivePrinciple(prescriptionId, showByActivePrinciple);
      const prescription = await PrescriptionService.fetchPrescriptionById(prescriptionId);
      await useInteractionStore.getState().fetchMedicineInteractions(prescription.idPrescricao);

      set((prevState) => {
        if (!prevState) return prevState;

        const prevPrescription = prevState.prescription;

        if (!prevPrescription) return prevState;

        const newPrescription = {
          ...prevPrescription,
          Itens: [...prescription.Itens],
          ExibirPorPrincipioAtivo: prescription.ExibirPorPrincipioAtivo,
        };

        return {
          ...prevState,
          prescription: newPrescription,
        };
      });
    } catch (e) {
      logger.error(e);
    }
  },

  addMedicineToPrescription: async (params: { medicine: Medicine; searchMedicineId: number | null }) => {
    const prescription = get().prescription;

    if (!prescription) return;

    const { medicine, searchMedicineId } = params;

    try {
      await MedicineService.addMedicineToPrescription(
        prescription.idPrescricao,
        adaptMedicineToAddPrescriptionParam(medicine),
        searchMedicineId
      );

      Tracker.trackEvent({
        event: 'Medication Included',
        parameters: {
          includedMedicationName: medicine.Label,
          idMedicamento: medicine.IdMedicamento,
        },
      });

      const updatedPrescription = await PrescriptionService.fetchPrescriptionById(prescription.idPrescricao);

      set((prev) => {
        const prevPrescription = prev.prescription;

        if (!prevPrescription) return prev;

        const newPrescription = {
          ...prevPrescription,
          Itens: [...updatedPrescription.Itens],
        };

        return {
          ...prev,
          prescription: newPrescription,
        };
      });

      useInteractionStore.getState().fetchMedicineInteractions(prescription.idPrescricao);
      toast.success('Medicamento incluído');
    } catch (e: any) {
      logger.error(e);
      toast.error('Não foi possível adicionar o medicamento, tente novamente');
      throw new Error(e.message);
    }
  },
  addMedicalReportToPrescription: async (data: MedicalReportDTO) => {
    const prescription = get().prescription;
    if (!prescription) return;

    try {
      const medicalReport = await MedicalReportService.create(prescription.idPrescricao, data);

      set((prevState) => {
        if (!prevState) return prevState;

        const prevPrescription = prevState.prescription;

        if (!prevPrescription) return prevState;

        const newPrescription = {
          ...prevPrescription,
          Relatorios: [...prevPrescription.Relatorios, medicalReport],
        };

        return {
          ...prevState,
          prescription: newPrescription,
        };
      });

      toast.success('Relatório incluído');
    } catch (e: any) {
      logger.error(e);
      toast.error('Não foi possível adicionar o relatório, tente novamente.');
      throw new Error(e.message);
    }
  },
  removeMedicalReportFromPrescription: async (medicalReportId: number) => {
    const prescription = get().prescription;
    if (!prescription) return;

    try {
      await MedicalReportService.delete(prescription.idPrescricao, medicalReportId);

      set((prevState) => {
        if (!prevState) return prevState;

        const prevPrescription = prevState.prescription;
        if (!prevPrescription) return prevState;

        const newPrescription = {
          ...prevPrescription,
          Relatorios: prevPrescription.Relatorios.filter(
            (medicalReport: MedicalReport) => medicalReport.idPrescricaoLaudoMedico !== medicalReportId
          ),
        };

        return {
          ...prevState,
          prescription: newPrescription,
        };
      });
    } catch (e: any) {
      logger.error(e);
      toast.error('Não foi possível excluir o relatório, tente novamente.');
      throw new Error(e.message);
    }
  },
  updatePrescriptionMedicalReport: async (data: MedicalReportDTO) => {
    try {
      const prescription = get().prescription;
      if (!prescription) return;

      const updatedMedicalReport = await MedicalReportService.update(prescription.idPrescricao, data);

      set((prevState) => {
        if (!prevState) return prevState;

        const prevPrescription = prevState.prescription;
        if (!prevPrescription) return prevState;

        const medicalReports = prevPrescription.Relatorios;

        const medicalReportInArray = medicalReports.find(
          (item) => item.idPrescricaoLaudoMedico === updatedMedicalReport.idPrescricaoLaudoMedico
        );

        if (!medicalReportInArray) return prevState;

        const newMedicalReportArray = medicalReports.slice();
        newMedicalReportArray.splice(medicalReports.indexOf(medicalReportInArray), 1, updatedMedicalReport);

        const newPrescription = {
          ...prevPrescription,
          Relatorios: newMedicalReportArray,
        };

        return {
          ...prevState,
          prescription: newPrescription,
        };
      });
    } catch (e: any) {
      logger.error(e);
      toast.error('Não foi possível atualizar o relatório, tente novamente.');
      throw new Error(e.message);
    }
  },
  addManualMedicineToPrescription: async (manualMedicine: ManualMedicine) => {
    const prescription = get().prescription;
    if (!prescription) return;

    try {
      const response = await MedicineService.addManualMedicineToPrescription({
        prescriptionId: prescription.idPrescricao,
        manualMedicine: manualMedicine,
        Manipulado: manualMedicine.Manipulado,
        TipoReceita: manualMedicine.TipoReceita,
      });

      Tracker.trackEvent({
        event: 'Manual Medication Included',
        parameters: {
          includedMedicationName: manualMedicine.Label,
          idMedicamentoManual: manualMedicine.idMedicamentoManual,
        },
      });

      if (!response) return;

      const updatedPrescription = await PrescriptionService.fetchPrescriptionById(prescription.idPrescricao);

      set((prev) => {
        const prevPrescription = prev.prescription;

        if (!prevPrescription) return prev;

        const newPrescription = {
          ...prevPrescription,
          ItensManuais: [...updatedPrescription.ItensManuais],
        };

        return {
          ...prev,
          prescription: newPrescription,
        };
      });

      toast.success('Medicamento incluído');
    } catch (e: any) {
      logger.error(e);
      toast.error('Não foi possível adicionar o medicamento manual, tente novamente');
      throw new Error(e.message);
    }
  },

  removeMedicalReferralFromPrescription: async (medicalReferralId: number) => {
    const prescription = get().prescription;
    if (!prescription) return;

    try {
      await PrescriptionService.removeMedicalReferral(prescription.idPrescricao, medicalReferralId);

      set((prevState) => {
        if (!prevState) return prevState;

        const prevPrescription = prevState.prescription;
        if (!prevPrescription) return prevState;

        const newPrescription = {
          ...prevPrescription,
          Encaminhamentos: prevPrescription.Encaminhamentos.filter(
            (medicalReferral: MedicalReferral) => medicalReferral.idEncaminhamentoPrescricao !== medicalReferralId
          ),
        };

        return {
          ...prevState,
          prescription: newPrescription,
        };
      });
    } catch (e: any) {
      logger.error(e);
      toast.error('Erro ao excluir o encaminhamento, tente novamente.');
      throw new Error(e.message);
    }
  },

  updateMedicalReferral: async (medicalReferral: MedicalReferral) => {
    const prescription = get().prescription;
    if (!prescription) return;

    try {
      const payload = {
        IdPrescricao: prescription.idPrescricao,
        idEncaminhamentoPrescricao: medicalReferral.idEncaminhamentoPrescricao,
        DataEncaminhamento: medicalReferral.DataEncaminhamento,
        Motivo: medicalReferral.Motivo,
        HistoriaClinica: medicalReferral.HistoriaClinica,
        Conduta: medicalReferral.Conduta,
        idEspecialidade: medicalReferral.Especialidade.IdEspecialidade,
        CID10: medicalReferral.CID10,
        DescricaoEspecialidade: medicalReferral.DescricaoEspecialidade,
        tipoParecer: medicalReferral.tipoParecer,
      };

      await PrescriptionService.updateMedicalReferral(prescription.idPrescricao, payload);

      set((prevState) => {
        if (!prevState) return prevState;

        const prevPrescription = prevState.prescription;
        if (!prevPrescription) return prevState;

        const medicalReferrals = prevPrescription.Encaminhamentos;

        const medicalReferralInArray = medicalReferrals.find(
          (item) => item.idEncaminhamentoPrescricao === medicalReferral.idEncaminhamentoPrescricao
        );

        if (!medicalReferralInArray) return prevState;

        const newMedicalReferralArray = medicalReferrals.slice();
        newMedicalReferralArray.splice(medicalReferrals.indexOf(medicalReferralInArray), 1, medicalReferral);

        const newPrescription = {
          ...prevPrescription,
          Encaminhamentos: newMedicalReferralArray,
        };

        return {
          ...prevState,
          prescription: newPrescription,
        };
      });
    } catch (e: any) {
      logger.error(e);
      toast.error('Erro ao atualizar o encaminhamento, tente novamente.');
      throw new Error(e.message);
    }
  },

  updatePrescriptionManualMedicine: async (manualMedicine: ManualMedicine) => {
    const prescription = get().prescription;
    if (!prescription) return;

    try {
      const response = await MedicineService.updatePrescriptionManualMedicine({
        manualMedicine: manualMedicine,
        prescriptionId: prescription.idPrescricao,
      });

      if (!response) return;

      const updatedPrescription = await PrescriptionService.fetchPrescriptionById(prescription.idPrescricao);

      set((prev) => {
        const prevPrescription = prev.prescription;

        if (!prevPrescription) return prev;

        const newPrescription = {
          ...prevPrescription,
          ItensManuais: [...updatedPrescription.ItensManuais],
        };

        return {
          ...prev,
          prescription: newPrescription,
        };
      });
    } catch (e: any) {
      logger.error(e);
      toast.error('Não foi possível atualizar o medicamento manual, tente novamente.');
      throw new Error(e.message);
    }
  },

  removePrescriptionManualMedicine: async (manualMedicine: ManualMedicine) => {
    const prescription = get().prescription;
    if (!prescription) return;

    try {
      const response = await MedicineService.removePrescriptionManualMedicine(
        prescription.idPrescricao,
        manualMedicine
      );

      const { trackingService } = await import('../shared/eventTracking');
      trackingService.trackPrescriptionItemRemove(manualMedicine);

      Tracker.trackEvent({
        event: 'Manual Medication Removed',
        parameters: {
          removedMedicationName: manualMedicine.Label,
          idPrescricaoMedicamentoManual: manualMedicine.idPrescricaoMedicamentoManual,
        },
      });

      if (!response) return;

      set((prevState) => {
        if (!prevState) return prevState;

        const prevPrescription = prevState.prescription;
        if (!prevPrescription) return prevState;

        const newPrescription = {
          ...prevPrescription,
          ItensManuais: prevPrescription.ItensManuais.filter(
            (m) => m.idPrescricaoMedicamentoManual !== manualMedicine.idPrescricaoMedicamentoManual
          ),
        };

        return {
          ...prevState,
          prescription: newPrescription,
        };
      });
    } catch (e: any) {
      logger.error(e);
      toast.error('Não foi possível remover o medicamento manual, tente novamente.');
      throw new Error(e.message);
    }
  },

  removeMedicineFromPrescription: async (medicine: Medicine) => {
    const prescription = get().prescription;
    if (!prescription) return;

    try {
      await MedicineService.removeMedicineFromPrescription(prescription.idPrescricao, medicine.idPrescricaoMedicamento);

      const { trackingService } = await import('../shared/eventTracking');
      trackingService.trackPrescriptionItemRemove(medicine);

      Tracker.trackEvent({
        event: 'Medication Removed',
        parameters: {
          removedMedicationName: medicine.Label,
          idPrescricaoMedicamento: medicine.idPrescricaoMedicamento,
        },
      });

      useInteractionStore.getState().fetchMedicineInteractions(prescription.idPrescricao);

      set((prevState) => {
        if (!prevState) return prevState;

        const prevPrescription = prevState.prescription;
        if (!prevPrescription) return prevState;

        const newPrescription = {
          ...prevPrescription,
          Itens: prevPrescription.Itens.filter(
            (m: Medicine) => m.idPrescricaoMedicamento !== medicine.idPrescricaoMedicamento
          ),
        };

        return {
          ...prevState,
          prescription: newPrescription,
        };
      });
    } catch (e: any) {
      logger.error(e);
      toast.error('Erro ao excluir o medicamento, tente novamente.');
      throw new Error(e.message);
    }
  },

  addExamToPrescription: async ({ exam, searchExamLogId }: { exam: Exam; searchExamLogId: number | null }) => {
    const prescription = get().prescription;
    if (!prescription) return;

    try {
      let examPayload = exam;
      if (!examPayload.tipoFormulario?.Chave || !examPayload.tipoFormulario?.idTipoExameFormulario) {
        examPayload.tipoFormulario = null;
      }

      const addedExam = await ExamService.addExamToPrescription({
        searchExamLogId,
        prescriptionId: prescription.idPrescricao,
        payload: examPayload,
      });

      Tracker.trackEvent({
        event: 'Exam Included',
        parameters: {
          ExamIncluded: addedExam.Nome,
          idExame: addedExam.idEzOrder,
        },
      });

      set((prevState) => {
        if (!prevState) return prevState;

        const prevPrescription = prevState.prescription;
        if (!prevPrescription) return prevState;

        const examWithId = {
          ...exam,
          idPrescricaoExame: addedExam.idPrescricaoExame,
        };

        const newPrescription = {
          ...prevPrescription,
          Exames: [...(prevPrescription.Exames || []), examWithId],
        };

        return {
          ...prevState,
          prescription: newPrescription,
        };
      });

      toast.success('Exame incluído');
    } catch (e: any) {
      logger.error(e);
      toast.error('Não foi possível adicionar o exame, tente novamente');
      throw new Error(e.message);
    }
  },

  updatePrescriptionMedicine: async (medicine: Medicine) => {
    const prescription = get().prescription;
    if (!prescription) return;

    try {
      await MedicineService.updatePrescriptionMedicine(
        prescription.idPrescricao,
        adaptMedicineToEditPrescriptionParam(medicine)
      );

      const updatedPrescription = await PrescriptionService.fetchPrescriptionById(prescription.idPrescricao);

      set((prevState) => {
        if (!prevState) return prevState;

        const prevPrescription = prevState.prescription;
        if (!prevPrescription) return prevState;

        const newPrescription = {
          ...prevPrescription,
          Itens: updatedPrescription.Itens,
        };

        return {
          ...prevState,
          prescription: newPrescription,
        };
      });
    } catch (e: any) {
      logger.error(e);
      toast.error('Erro ao atualizar o medicamento, tente novamente.');
      throw new Error(e.message);
    }
  },

  removeExamFromPrescription: async (exam: Exam) => {
    const prescription = get().prescription;
    if (!prescription || !exam.idPrescricaoExame) return;

    try {
      await ExamService.removeExamFromPrescription(exam.idPrescricaoExame);

      const { trackingService } = await import('../shared/eventTracking');
      trackingService.trackPrescriptionItemRemove(exam);

      Tracker.trackEvent({
        event: 'Exam Removed',
        parameters: {
          removedExamName: exam.Nome,
          idPrescricaoExame: exam.idPrescricaoExame,
        },
      });

      set((prevState) => {
        if (!prevState) return prevState;

        const prevPrescription = prevState.prescription;
        if (!prevPrescription) return prevState;

        const newPrescription = {
          ...prevPrescription,
          Exames: prevPrescription.Exames.filter((e) => e.idPrescricaoExame !== exam.idPrescricaoExame),
        };

        return {
          ...prevState,
          prescription: newPrescription,
        };
      });
    } catch (e: any) {
      logger.error(e);
      toast.error('Erro ao excluir exame, tente novamente');
      throw new Error(e.message);
    }
  },

  updatePrescriptionExam: async (exam: Exam) => {
    const prescription = get().prescription;
    if (!prescription || !exam.idPrescricaoExame) return;

    try {
      const examPayload: Exam = { ...exam, tipoFormulario: exam.tipoFormulario?.Chave ? exam.tipoFormulario : null };
      await ExamService.updateExamFromPrescription(exam.idPrescricaoExame, examPayload);

      set((prevState) => {
        if (!prevState) return prevState;

        const prevPrescription = prevState.prescription;
        if (!prevPrescription) return prevState;

        const exams = prevPrescription.Exames;

        const examInArray = exams.find((e) => e.idPrescricaoExame === exam.idPrescricaoExame);

        if (!examInArray) return prevState;

        const newExams = exams.slice();
        newExams.splice(exams.indexOf(examInArray), 1, exam);

        const newPrescription = {
          ...prevPrescription,
          Exames: newExams,
        };

        return {
          ...prevState,
          prescription: newPrescription,
        };
      });
    } catch (e: any) {
      logger.error(e);
      toast.error('Erro ao atualizar o exame, tente novamente.');
      throw new Error(e.message);
    }
  },

  addMedicalCertificateToPrescription: async (data: MedicalCertificateDTO) => {
    const prescription = get().prescription;
    if (!prescription) return;

    try {
      const medicalCertificate = await MedicalCertificateService.create(prescription.idPrescricao, data);
      set((prevState) => {
        if (!prevState) return prevState;

        const prevPrescription = prevState.prescription;
        if (!prevPrescription) return prevState;

        const newPrescription = {
          ...prevPrescription,
          Atestados: [...prevPrescription.Atestados, medicalCertificate],
        };

        return {
          ...prevState,
          prescription: newPrescription,
        };
      });
      toast.success('Atestado incluído');
    } catch (e: any) {
      logger.error(e);
      toast.error('Não foi possível adicionar o atestado, tente novamente.');
      throw new Error(e.message);
    }
  },

  removeMedicalCertificateFromPrescription: async (prescriptionMedicalCertificateId: number) => {
    const prescription = get().prescription;
    if (!prescription) return;

    try {
      await MedicalCertificateService.delete(prescriptionMedicalCertificateId);

      set((prevState) => {
        if (!prevState) return prevState;

        const prevPrescription = prevState.prescription;
        if (!prevPrescription) return prevState;

        const remainingMedicalCertificates = prevPrescription.Atestados.filter(
          (medicalCertificate: MedicalCertificate) =>
            medicalCertificate.idPrescricaoAtestado !== prescriptionMedicalCertificateId
        );

        const isRemovingAtestado =
          prevPrescription.Atestado &&
          prevPrescription.Atestado.idPrescricaoAtestado === prescriptionMedicalCertificateId;
        const firstRemainingMedicalCertificateOrNull = LodashGet(remainingMedicalCertificates, '[0]', null);

        const newPrescription = {
          ...prevPrescription,
          Atestado: isRemovingAtestado ? firstRemainingMedicalCertificateOrNull : prevPrescription.Atestado,
          Atestados: remainingMedicalCertificates,
        };

        return {
          ...prevState,

          prescription: newPrescription,
        };
      });
    } catch (e: any) {
      logger.error(e);
      toast.error('Não foi possível excluir o atestado, tente novamente.');
      throw new Error(e.message);
    }
  },

  updatePrescriptionMedicalCertificate: async (data: MedicalCertificateDTO) => {
    const prescription = get().prescription;
    if (!prescription) return;

    try {
      const updatedMedicalCertificate = await MedicalCertificateService.update(data.medicalCertificateId, data);

      if (data.icd === null) {
        updatedMedicalCertificate.CID10Estruturado = [];
      }

      set((prevState) => {
        if (!prevState) return prevState;

        const prevPrescription = prevState.prescription;
        if (!prevPrescription) return prevState;

        const medicalCertificates = prevPrescription.Atestados;

        const medicalCertificateInArray = medicalCertificates.find(
          (item) => item.idPrescricaoAtestado === updatedMedicalCertificate.idPrescricaoAtestado
        );

        if (!medicalCertificateInArray) return prevState;

        const newMedicalCertificateArray = medicalCertificates.slice();
        newMedicalCertificateArray.splice(
          medicalCertificates.indexOf(medicalCertificateInArray),
          1,
          updatedMedicalCertificate
        );

        const newPrescription = {
          ...prevPrescription,
          Atestados: newMedicalCertificateArray,
        };

        return {
          ...prevState,
          prescription: newPrescription,
        };
      });
    } catch (e: any) {
      logger.error(e);
      toast.error('Não foi possível atualizar o atestado, tente novamente.');
      throw new Error(e.message);
    }
  },

  addMedicalReferralToPrescription: async (medicalReferral: MedicalReferral) => {
    const prescription = get().prescription;
    if (!prescription) return;

    try {
      const payload: MedicalReferralDTO = {
        idEncaminhamentoPrescricao: medicalReferral.idEncaminhamentoPrescricao,
        DataEncaminhamento: medicalReferral.DataEncaminhamento,
        Motivo: medicalReferral.Motivo,
        HistoriaClinica: medicalReferral.HistoriaClinica,
        Conduta: medicalReferral.Conduta,
        idEspecialidade: medicalReferral.Especialidade.IdEspecialidade,
        CID10: medicalReferral.CID10,
        DescricaoEspecialidade: medicalReferral.DescricaoEspecialidade,
        tipoParecer: medicalReferral.tipoParecer,
      };

      Tracker.trackEvent({
        event: 'Medical Referral Letter Included',
        parameters: {
          MedicalSpecialty: medicalReferral.Especialidade,
        },
      });

      await PrescriptionService.createMedicalReferral(prescription.idPrescricao, payload);

      const updatedPrescription = await PrescriptionService.fetchPrescriptionById(prescription.idPrescricao);

      set((prevState) => {
        if (!prevState) return prevState;

        const prevPrescription = prevState.prescription;
        if (!prevPrescription) return prevState;

        const newPrescription = {
          ...prevPrescription,
          Encaminhamentos: [...updatedPrescription.Encaminhamentos],
        };

        return {
          ...prevState,
          prescription: newPrescription,
        };
      });
      toast.success('Encaminhamento incluído');
    } catch (e: any) {
      logger.error(e);
      toast.error('Não foi possível adicionar o encaminhamento, tente novamente');
      throw new Error(e.message);
    }
  },
  prescriptionObservation: () => get().prescription?.Comentario || '',

  updatePrescriptionObservation: (observation: string | null) => {
    set((prevState) => {
      if (!prevState) return prevState;

      const prevPrescription = prevState.prescription;
      if (!prevPrescription) return prevState;

      const newPrescription = {
        ...prevPrescription,
        Comentario: observation || '',
      };

      return {
        ...prevState,
        prescription: newPrescription,
      };
    });
  },
  onUpdatePrescriptionObservation: async (observation: string | null) => {
    const prescription = get().prescription;
    if (!prescription) return;

    try {
      await PrescriptionService.updatePrescriptionObservation(prescription.idPrescricao, observation);

      set((prevState) => {
        if (!prevState) return prevState;

        const prevPrescription = prevState.prescription;
        if (!prevPrescription) return prevState;

        const newPrescription = {
          ...prevPrescription,
          Comentario: observation || '',
        };

        return {
          ...prevState,
          prescription: newPrescription,
        };
      });
    } catch (error) {
      toast.error('Não foi possível adicionar a observação, tente novamente.');
    }
  },
  prescriptionItemsAmount: () => {
    const prescription = get().prescription;

    if (!prescription) return 0;
    let totalItens = 0;

    if (prescription.Atestado && isEmpty(prescription.Atestados)) {
      totalItens = +1;
    }

    totalItens +=
      prescription.ItensManuais.length +
      prescription.Itens.length +
      prescription.Exames.length +
      prescription.Encaminhamentos.length +
      prescription.Atestados.length +
      prescription.Relatorios.length +
      prescription.Formulas.length;

    return totalItens;
  },

  addItemsFromPrescription: async (
    currentPrescriptionId: number,
    originPrescriptionId: number,
    removeItems: boolean,
    addLmeFromOriginPrescription: boolean
  ) => {
    try {
      await PrescriptionService.addItemsFromPrescription(
        currentPrescriptionId,
        originPrescriptionId,
        removeItems,
        addLmeFromOriginPrescription
      );

      const updatedPrescription = await PrescriptionService.fetchPrescriptionById(currentPrescriptionId);

      set((prevState) => {
        if (!prevState) return prevState;

        return {
          ...prevState,
          prescription: updatedPrescription,
        };
      });

      useInteractionStore.getState().fetchMedicineInteractions(currentPrescriptionId);

      if (get().prescriptionItemsAmount() === 0) {
        toast.success('Itens Adicionados');
        return;
      }

      toast.success(removeItems ? 'Itens Substituídos' : 'Itens Adicionados');
    } catch (error: any) {
      toast.error('Não foi possível adicionar os itens à prescrição, tente novamente.');
      throw new Error(error.message);
    }
  },

  partnerJustifyInteraction: () => LodashGet(get().prescriberConfiguration, 'Features.enableInteractionJustify', false),
  enableTussCodeOnInterface: () =>
    LodashGet(get().prescriberConfiguration, 'Features.enableTussCodeOnInterface', false),

  simplePrescriptionLayouts: [],
  setSimplePrescriptionLayouts: (layouts: PDFModelLayout[]) => {
    set((prev) => {
      return {
        ...prev,
        simplePrescriptionLayouts: layouts,
      };
    });
  },

  specialPrescriptionLayouts: [],
  setSpecialPrescriptionLayouts: (layouts: PDFModelLayout[]) => {
    set((prev) => {
      return {
        ...prev,
        specialPrescriptionLayouts: layouts,
      };
    });
  },

  isPrescriptionClosed: false,
  setIsPrescriptionClosed: (value: boolean) => {
    set((prev) => {
      return {
        ...prev,
        isPrescriptionClosed: value,
      };
    });
  },
  fetchPdfLayoutModels: async (): Promise<void> => {
    const prescriber = get().prescriber();
    const prescription = get().prescription;

    if (!prescriber || !prescription) return;

    const layouts = await PrescriberService.getPdfLayouts({
      prescriberId: prescriber.idPrescritor,
      prescriptionId: prescription.idPrescricao,
    });

    const specialPrescriptionLayouts = layouts.filter((layout) => layout.tipo === 'CONTROLADO');
    const simplePrescriptionLayouts = layouts.filter((layout) => layout.tipo === 'SIMPLES');

    set((prev) => {
      return {
        ...prev,
        simplePrescriptionLayouts: simplePrescriptionLayouts,
        specialPrescriptionLayouts: specialPrescriptionLayouts,
      };
    });
  },

  digitalCertificateIsRequired: () => {
    return LodashGet(get().prescriberConfiguration, 'Features.mandatoryDigitalCertificate', false);
  },

  updatePatientAndIcdData: async () => {
    const prescription = get().prescription;

    if (!prescription) return;
    const updatedPrescription = await PrescriptionService.fetchPrescriptionById(prescription.idPrescricao);

    set((prevState) => {
      if (!prevState) return prevState;

      const prevPrescription = prevState.prescription;
      if (!prevPrescription) return prevState;

      const newPrescription = {
        ...prevPrescription,
        Paciente: {
          ...prevState.prescription?.Paciente,
          ...updatedPrescription.Paciente,
        },
        CIDS10Estruturados: updatedPrescription.CIDS10Estruturados,
        Diagnosticos: updatedPrescription.Diagnosticos,
      };

      return {
        ...prevState,
        prescription: newPrescription,
      };
    });

    useInteractionStore.getState().fetchMedicineInteractions(prescription.idPrescricao);
  },

  cancelOrContinueLater: async (confirmation: boolean, reason?: string) => {
    const prescription = get().prescription;

    if (!prescription) return;

    if (confirmation === false) {
      sendCancelPostMessage();
      Tracker.trackEvent({
        event: 'Prescription Finish Later Button Clicked',
      });

      const prescriberConfiguration = get().prescriberConfiguration;
      amplitudeModule.prescriptionDraftSaved({
        prescrition_time: new Date().toISOString(),
        validation_code: prescription.CodigoValidacao ?? '',
        partner_id: prescriberConfiguration?.idParceiro ?? 0,
        partner_name: prescriberConfiguration?.Nome ?? '',
      });
    }

    if (confirmation === true) {
      try {
        await PrescriptionService.cancelPrescription(prescription.idPrescricao, reason);
        sendExcludedPostMessage();
        Tracker.trackEvent({
          event: 'Prescription Excluded',
        });
      } catch (e) {
        toast.error('Não foi possível cancelar a prescrição, tente novamente.');
      }
    }

    clearPrescriptionStartedPayload(prescription.idPrescricao);
    clearPrescriptionCompletedPayload(prescription.idPrescricao);
    clearConcomitanceMap(prescription.idPrescricao);
    clearSuggestedPosologyMap(prescription.idPrescricao);
  },

  closePrescription: async (payload: ClosePrescriptionPayload): Promise<ClosePrescriptionResponse> => {
    const prescription = get().prescription;

    if (!prescription || !prescription.Paciente)
      return { closed: false, errorMessage: 'Faltando informações sobre a prescrição' };

    try {
      let signData = null;

      if (payload.certificateType === CERTIFICATES.SOLUTI) {
        const hasValidSolutiSession = await CertificateService.checkSolutiStatus(prescription.idPrescricao);

        if (!hasValidSolutiSession) {
          const autenticated = await authenticateSoluti({
            targetId: 'PrescriptionEmission',
            prescriptionId: prescription.idPrescricao,
          });
          if (!autenticated) return { closed: false, errorMessage: 'Falha ao autenticar' };
        }

        signData = {
          provider: 'SOLUTI',
        };
      }

      if (payload.certificateType === CERTIFICATES.VALID) {
        signData = {
          provider: 'VIDAAS',
        };
      }

      if (payload.certificateType === CERTIFICATES.SAFEWEB) {
        const session = await CertificateService.safeweb.checkSession(prescription.idPrescricao);

        if (!session.SessaoAtiva) {
          const authenticated = await authenticateSafeweb({
            targetId: 'PrescriptionEmission',
            prescriptionId: prescription.idPrescricao,
          });
          if (!authenticated) return { closed: false, errorMessage: 'Falha ao autenticar' };
        }

        signData = {
          provider: CERTIFICATES.SAFEWEB,
        };
      }

      if (payload.certificateType === CERTIFICATES.CERTISIGN) {
        signData = {
          provider: 'CERTISIGN',
        };
      }

      if (payload.certificateType === CERTIFICATES.REMOTE_ID) {
        signData = {
          provider: CERTIFICATES.REMOTE_ID,
        };
      }

      if (payload.certificateType === CERTIFICATES.LACUNA_MV_BIOSIGNER) {
        signData = {
          provider: CERTIFICATES.LACUNA_MV_BIOSIGNER,
        };
      }

      if (payload.certificateType === CERTIFICATES.LOCAL) {
        const closed = await get().closePrescriptionWithLocalCertificate(payload);
        return { closed, errorMessage: '' };
      }

      if (payload.certificateType === CERTIFICATES.EVAL_CONTEXT) {
        signData = {
          provider: CERTIFICATES.EVAL_CONTEXT,
          pin: LodashGet(payload, 'certificateAuthentication.pin', null),
        };
      }

      if (payload.certificateType === CERTIFICATES.DINAMO_UNIMEDBH) {
        signData = {
          provider: CERTIFICATES.DINAMO_UNIMEDBH,
          pin: LodashGet(payload, 'certificateAuthentication.pin', null),
        };
      }

      if (payload.certificateType === CERTIFICATES.DINAMO) {
        signData = {
          provider: CERTIFICATES.DINAMO,
        };
      }

      const patientDocument = payload.patientData?.document || prescription.Paciente.Documento;
      const hasDigitalCertificates = payload.hasDigitalCertificates;

      const updatePrescriptionPayload: UpdatePrescriptionPayload = {
        Comentario: payload.prescriptionObservation,
        ComentarioExame: '',
        Emails: [payload.email],
        idPrescricao: prescription.idPrescricao,
        ImpressaoHabilitada: payload.generatePdf,
        Telefones: [payload.phone],
        status: PRESCRIPTION_STATUS.EMITTED,
        patientData: {
          address: payload.patientData?.address,
          gender: payload.patientData?.gender,
          birthDate: payload.patientData?.birthDate,
          icd10structured: payload.patientData?.icd10structured,
          document: patientDocument,
          passport: payload.patientData?.passport,
        },
        signData,
        showDateInPdf: payload.showDateInPdf,
        emittedTo: payload.emittedTo,
        issueMedicinesInSeparateTabs: payload.issueMedicinesInSeparateTabs,
        hasDigitalCertificates,
        skipDefaultRqe: payload.skipDefaultRqe,
      };

      if (payload.establishment) {
        updatePrescriptionPayload.establishment = payload.establishment;
      }

      const updatedPrescription = await PrescriptionService.updatePrescription(updatePrescriptionPayload);

      get().finishPrescriptionEmission(
        payload.generatePdf,
        payload.openPatientApp,
        payload.shouldSendPrescriptionWhatsApp,
        payload.phone
      );

      get().setPrescription(updatedPrescription);

      const completedPayload = getPrescriptionCompletedPayload(prescription.idPrescricao);
      if (completedPayload) {
        const sentPayload = buildPrescriptionSentPayload({
          completedPayload,
          prescription: updatedPrescription,
          generatePdf: payload.generatePdf,
          certificateType: payload.certificateType,
          showDateInPdf: payload.showDateInPdf,
          emittedTo: payload.emittedTo,
          issueMedicinesInSeparateTabs: payload.issueMedicinesInSeparateTabs,
          email: payload.email,
          isDigitalSignMandatory: updatedPrescription.CertificadoDigitalObrigatorio ?? false,
          isPdfCustomized: !!(updatedPrescription.LogoURL || updatedPrescription.CorPrimaria),
          isPersonalNumberSent: !payload.hasDigitalCertificates,
          hasAddressRequired: !!payload.patientData?.address,
        });
        amplitudeModule.prescriptionSent(sentPayload);
      }

      clearPrescriptionStartedPayload(prescription.idPrescricao);

      return { closed: true, errorMessage: '' };
    } catch (e: any) {
      if (e?.response?.data?.some((err: any) => err.Entidade === 'CERTIFICATE')) {
        return { closed: false, errorMessage: '', hasCertificateError: true };
      }
      if (e.code === 'ECONNABORTED') {
        try {
          const closed = await get().prescriptionEmissionFallBackOnTimeout(
            payload.generatePdf,
            payload.openPatientApp,
            payload.shouldSendPrescriptionWhatsApp,
            payload.phone
          );
          return { closed, errorMessage: '' };
        } catch (e) {
          // se ocorrer um erro dentro do fallback podemos mostrar a mensagem generica de erro.
        }
      }

      let errorMessage = LodashGet(
        e,
        'response.data[0].Descricao',
        LodashGet(e, 'response.data.Descricao', 'Não foi possível finalizar a prescrição, tente novamente.')
      );

      if (typeof e === 'string') {
        errorMessage = e;
      }

      logger.error(e);
      return { closed: false, errorMessage };
    }
  },

  closePrescriptionWithLocalCertificate: async (payload: ClosePrescriptionPayload): Promise<boolean> => {
    const prescription = get().prescription;

    if (!prescription || !prescription.Paciente) return false;

    const localCertificate = payload.localCertificate;
    if (!localCertificate) return false;

    const patientDocument = payload.patientData?.document || prescription.Paciente.Documento;

    await PrescriptionService.updatePrescription({
      Comentario: payload.prescriptionObservation,
      ComentarioExame: '',
      Emails: [payload.email],
      idPrescricao: prescription.idPrescricao,
      ImpressaoHabilitada: payload.generatePdf,
      Telefones: [payload.phone],
      status: PRESCRIPTION_STATUS.INITIADED,
      patientData: {
        address: payload.patientData?.address,
        gender: payload.patientData?.gender,
        birthDate: payload.patientData?.birthDate,
        document: patientDocument,
        passport: payload.patientData?.passport,
      },
      showDateInPdf: payload.showDateInPdf,
      emittedTo: payload.emittedTo,
      issueMedicinesInSeparateTabs: payload.issueMedicinesInSeparateTabs,
      hasDigitalCertificates: payload.hasDigitalCertificates,
    });

    const response = await PrescriptionService.localSignPrescription(prescription.idPrescricao, {
      name: localCertificate.subjectName,
      alwaysUse: true,
      thumbprint: localCertificate.thumbprint,
      content: await LacunaService.readCertificate(localCertificate.thumbprint),
      ownerDocument: localCertificate.pkiBrazil.cpf,
    });

    const tokens = [];

    if (response.medicamentoToken) {
      tokens.push({
        token: response.medicamentoToken.Token,
        documentType: 'PRESCRICAO',
      });
    }

    if (response.exameToken) {
      tokens.push({
        token: response.exameToken.Token,
        documentType: 'EXAME',
      });
    }

    if (response.atestadoToken) {
      tokens.push({
        token: response.atestadoToken.Token,
        documentType: 'ATESTADO',
      });
    }

    if (response.encaminhamentoToken) {
      tokens.push({
        token: response.encaminhamentoToken.Token,
        documentType: 'ENCAMINHAMENTO',
      });
    }

    if (response.relatorioToken) {
      tokens.push({
        token: response.relatorioToken.Token,
        documentType: 'RELATORIO',
      });
    }

    if (response.lmeToken) {
      tokens.push({
        token: response.lmeToken.Token,
        documentType: 'LME',
      });
    }

    if (tokens.length === 0) return false;

    await LacunaService.signWithTokens(
      localCertificate.thumbprint,
      tokens.map((token) => token.token)
    );

    await PrescriptionService.updatePrescription({
      Comentario: payload.prescriptionObservation,
      ComentarioExame: '',
      Emails: [payload.email],
      idPrescricao: prescription.idPrescricao,
      ImpressaoHabilitada: payload.generatePdf,
      Telefones: [payload.phone],
      status: PRESCRIPTION_STATUS.EMITTED,
      patientData: {
        address: payload.patientData?.address,
        gender: payload.patientData?.gender,
        birthDate: payload.patientData?.birthDate,
        document: patientDocument || '',
        passport: payload.patientData?.passport,
      },
      signData: {
        provider: 'LOCAL',
        tokens,
      },
      showDateInPdf: payload.showDateInPdf,
      emittedTo: payload.emittedTo,
      issueMedicinesInSeparateTabs: payload.issueMedicinesInSeparateTabs,
      hasDigitalCertificates: payload.hasDigitalCertificates,
    });

    const emissionResult = await get().finishPrescriptionEmission(
      payload.generatePdf,
      payload.openPatientApp,
      payload.shouldSendPrescriptionWhatsApp,
      payload.phone
    );

    const completedPayloadLocal = getPrescriptionCompletedPayload(prescription.idPrescricao);
    if (completedPayloadLocal) {
      const sentPayloadLocal = buildPrescriptionSentPayload({
        completedPayload: completedPayloadLocal,
        prescription,
        generatePdf: payload.generatePdf,
        certificateType: payload.certificateType,
        showDateInPdf: payload.showDateInPdf,
        emittedTo: payload.emittedTo,
        issueMedicinesInSeparateTabs: payload.issueMedicinesInSeparateTabs,
        email: payload.email,
        isDigitalSignMandatory: prescription.CertificadoDigitalObrigatorio ?? false,
        isPdfCustomized: !!(prescription.LogoURL || prescription.CorPrimaria),
        isPersonalNumberSent: !payload.hasDigitalCertificates,
        hasAddressRequired: !!payload.patientData?.address,
      });
      amplitudeModule.prescriptionSent(sentPayloadLocal);
    }

    return emissionResult;
  },

  prescriptionEmissionFallBackOnTimeout: async (
    generatePdf: boolean,
    openPatientApp: boolean,
    shouldSendPrescriptionWhatsApp: boolean,
    phoneNumber: string
  ): Promise<boolean> => {
    const prescription = get().prescription;

    const isPrescriptionClosed = get().isPrescriptionClosed;

    if (isPrescriptionClosed) {
      return true;
    }

    if (!prescription) {
      return false;
    }

    let didClose = false;
    const checkPrescriptionStatus = async () => {
      for (let tentative = 0; tentative < 10; tentative++) {
        const statusData = await PrescriptionService.getPrescriptionStatus(prescription.idPrescricao);

        if (statusData.Status === 'Emitida') {
          didClose = await get().finishPrescriptionEmission(
            generatePdf,
            openPatientApp,
            shouldSendPrescriptionWhatsApp,
            phoneNumber
          );
          return;
        }

        await sleep(3000);
        didClose = false;
      }
    };
    await checkPrescriptionStatus();
    return didClose;
  },

  finishPrescriptionEmission: async (
    generatePdf: boolean,
    openPatientApp: boolean,
    shouldSendPrescriptionWhatsApp: boolean,
    phoneNumber: string
  ) => {
    const prescription = get().prescription;
    const isPrescriptionClosed = get().isPrescriptionClosed;

    if (!prescription || isPrescriptionClosed) return false;

    const updatedPrescription = await PrescriptionService.fetchPrescriptionById(prescription.idPrescricao);

    set((prevState) => {
      if (!prevState) return prevState;

      return {
        ...prevState,
        isPrescriptionClosed: true,
      };
    });

    const examStore = useExamStore.getState();
    const completedPayload = buildPrescriptionCompletedPayload(updatedPrescription, examStore);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    amplitudeModule.prescriptionCompleted(completedPayload as any);
    clearPrescriptionCompletedPayload(prescription.idPrescricao);
    clearConcomitanceMap(prescription.idPrescricao);
    clearSuggestedPosologyMap(prescription.idPrescricao);
    clearProtocolMap(prescription.idPrescricao);

    Cookies.remove(`prescription-${prescription.ReferenciaInterna}-jwt`);

    await get().postClosePrescriptionMessage(updatedPrescription);

    if (generatePdf) {
      await get().openPrescriptionDocuments(updatedPrescription.Documentos);
    }

    if (openPatientApp) {
      await get().openPatientAppWindow(updatedPrescription.UrlPacientes, updatedPrescription.CodigoValidacao);
    }

    if (shouldSendPrescriptionWhatsApp) {
      get().openWhatsAppToSendPrescription(prescription, phoneNumber, phoneNumber.substring(phoneNumber.length - 4));
    }
    return true;
  },

  openWhatsAppToSendPrescription: (prescription: Prescription, phoneNumber: string, accessCode: string) => {
    if (!prescription || !prescription.Paciente || !prescription.Prescritor) return;

    const name = LodashGet(prescription.Paciente, 'Nome', null);

    if (!name) return;

    const nxpLink = new URL(`${process.env.REACT_APP_NXPACIENTES_URL}/${prescription.CodigoValidacao}`);

    nxpLink.searchParams.append('utm_source', 'whatsapp');
    nxpLink.searchParams.append('s', 'w');

    const msgParams = buildWhatsappMessageParams(prescription, nxpLink.toString(), accessCode);
    if (!msgParams) return;

    const message = buildWhatsappMessage(msgParams);

    const params = new URLSearchParams();
    params.append('phone', `+55${phoneNumber}`);
    params.append('text', message);

    // Obtendo a string da query
    const paramsUrl = `send?${params.toString()}`;

    sendWhatsappPostMessage(paramsUrl);
    window.open(`https://api.whatsapp.com/${paramsUrl}`, '_blank', 'noopener');
  },

  openPrescriptionDocuments: (documents: PrescriptionDocument[]) => {
    documents.forEach((document) => {
      window.open(document.URL, '_blank', 'noopener');
    });
  },

  openPatientAppWindow: (patientAppUrl: string, validationCode: string) => {
    window.open(`${patientAppUrl}${validationCode}?utm_source=opened_by_the_doctor`, '_blank', 'noopener');
  },

  postClosePrescriptionMessage: (updatedPrescription: Prescription) => {
    const prescription = get().prescription;

    if (!prescription) return;

    const prescriptionClose: Prescription = { ...prescription };

    const updatedFields: Partial<Prescription> = pick(updatedPrescription, [
      'PrescricaoAssinada',
      'CodigoValidacao',
      'URLBarCode',
      'URLQRCode',
      'PDFUrl',
    ]);

    Object.keys(updatedFields).forEach((key) => {
      // @ts-ignore
      prescriptionClose[key] = updatedFields[key];
    });

    const returnPayload = {
      ...updatedPrescription,
      Medicamentos: prescriptionClose.Itens,
      MedicamentosManuais: prescriptionClose.ItensManuais,
      type: 'prescricao',
      EnviarSMS: true,
    };

    if (window.opener) {
      setTimeout(function () {
        if (window === null || window.opener === null) return;
        window.opener.postMessage(JSON.parse(JSON.stringify(returnPayload)), '*');
      }, 4000);
    }

    if (window.location !== window.parent.location) {
      setTimeout(function () {
        window.parent.postMessage(JSON.parse(JSON.stringify(returnPayload)), '*');
      }, 4000);
    }
  },

  logoUrl: () => {
    const prescription = get().prescription;

    if (!prescription) return null;
    return prescription.LogoURL || null;
  },

  shouldBypassCertificateFlow: false,

  fetchPrescriptionByReference: async (referenceCode: string | null): Promise<Prescription | null> => {
    if (!referenceCode) return null;

    const [prescription] = await Promise.all([
      get().getAndSetPrescriptionByReference(referenceCode),
      get().getAndSetPrescriberConfigurations(referenceCode),
    ]);

    await get().getAndSetBypass();
    return prescription;
  },

  getAndSetBypass: async () => {
    const prescription = get().prescription;
    if (!prescription) return;

    const hasBypass = await PrescriptionService.getBypass(prescription.idPrescritor);

    set((prevState) => {
      if (!prevState) return prevState;

      return {
        ...prevState,
        shouldBypassCertificateFlow: hasBypass,
      };
    });
  },

  getAndSetPrescriberConfigurations: async (referenceCode: string) => {
    if (!referenceCode) return;

    const prescriberConfigurations = await PrescriberService.getConfigurations(referenceCode);

    set((prevState) => {
      if (!prevState) return prevState;

      return {
        ...prevState,
        prescriberConfiguration: prescriberConfigurations,
      };
    });

    safeSetInLocalStorage('prescriberConfigurations', JSON.stringify(prescriberConfigurations));
  },

  prescriptionTokenExpirationData: null,
  setPrescriptionTokenExpirationData: (value: TokenExpirationData | null) => {
    set((prev) => {
      return {
        ...prev,
        prescriptionTokenExpirationData: value,
      };
    });
  },

  getAndSetPrescriptionByReference: async (referenceCode: string): Promise<Prescription | null> => {
    if (!referenceCode) return null;
    const { prescription, tokenExpirationData } = await PrescriptionService.fetchPrescription(referenceCode);

    if (tokenExpirationData) {
      set((prev) => {
        return {
          ...prev,
          prescriptionTokenExpirationData: tokenExpirationData,
        };
      });
    }

    set((prev) => {
      return {
        ...prev,
        prescription: prescription,
      };
    });

    setSurvicate({
      idMedico: prescription.Prescritor.idPrescritor,
      codigoValidacao: prescription.CodigoValidacao,
    });

    if (!isEmpty(prescription.Itens)) {
      useInteractionStore.getState().fetchMedicineInteractions(prescription.idPrescricao);
    }

    return prescription;
  },

  primaryColor: () => {
    const prescription = get().prescription;

    if (!prescription) {
      return DEFAULT_PRIMARY_COLOR;
    }

    const handledPrescriptionPrimaryColor = colorHandler(prescription.CorPrimaria || '', DEFAULT_PRIMARY_COLOR);

    const handledPrimaryColorFromLocalStorage = colorHandler(
      safeGetFromLocalStorage('primaryColor') || '',
      DEFAULT_PRIMARY_COLOR,
      () => {
        safeDeleteFromLocalStorage('primaryColor');
      }
    );

    const primaryColorFromLocalStorage = safeGetFromLocalStorage('primaryColor');

    if (handledPrescriptionPrimaryColor) {
      if (
        handledPrimaryColorFromLocalStorage !== primaryColorFromLocalStorage ||
        primaryColorFromLocalStorage !== handledPrescriptionPrimaryColor
      ) {
        safeSetInLocalStorage('primaryColor', handledPrescriptionPrimaryColor);
      }

      return handledPrescriptionPrimaryColor;
    }

    return DEFAULT_PRIMARY_COLOR;
  },

  secondaryColor: () => {
    const prescription = get().prescription;

    if (!prescription) {
      return DEFAULT_SECONDARY_COLOR;
    }

    const handledPrescriptionSecondaryColor = colorHandler(prescription.CorSecundaria || '', DEFAULT_SECONDARY_COLOR);

    const handledSecondaryColorFromLocalStorage = colorHandler(
      safeGetFromLocalStorage('secondaryColor') || '',
      DEFAULT_SECONDARY_COLOR,
      () => {
        safeDeleteFromLocalStorage('secondaryColor');
      }
    );

    const secondaryColorFromLocalStorage = safeGetFromLocalStorage('secondaryColor');

    if (handledPrescriptionSecondaryColor) {
      if (
        handledSecondaryColorFromLocalStorage !== secondaryColorFromLocalStorage ||
        secondaryColorFromLocalStorage !== handledPrescriptionSecondaryColor
      ) {
        safeSetInLocalStorage('secondaryColor', handledPrescriptionSecondaryColor);
      }

      return handledPrescriptionSecondaryColor;
    }

    return DEFAULT_SECONDARY_COLOR;
  },

  ownedCertificates: [],
  setOwnedCertificates: (certificates: OwnedCertificate[]) => {
    set((prev) => {
      return {
        ...prev,
        ownedCertificates: certificates,
      };
    });
  },

  selectedCertificateType: null,
  setSelectedCertificateType: (certificates: CERTIFICATES | null) => {
    set((prev) => {
      return {
        ...prev,
        selectedCertificateType: certificates,
      };
    });
  },

  getAndSetPrescriberCertificates: async (params: GetAndSetPrescriberCertificatesParams) => {
    try {
      const ownedCertificates = await PrescriberService.getOwnedCertificates(
        params.prescriberDocument,
        params.prescriptionId
      );

      set((prev) => {
        return {
          ...prev,
          ownedCertificates: ownedCertificates,
        };
      });

      const availableCertificates = ownedCertificates
        .filter((certificate) => certificate.status === 'S')
        .map((certificate) => certificate.name);

      // assina via prontuario, logo, não realizamos as regras de certificados
      if (get().prescriberConfiguration?.AssinarViaProntuario) {
        set((prev) => {
          return {
            ...prev,
            selectedCertificateType: null,
            hideManualSigningMethod: false,
          };
        });
        return;
      }

      if (
        params.lastSignatureMethod === CERTIFICATES.LOCAL &&
        !/Mobi|Android|iPhone|iPad|iPod|Windows Phone/i.test(navigator.userAgent)
      ) {
        const dataToSave = {
          selectedCertificateType: CERTIFICATES.LOCAL,
          hideManualSigningMethod: false,
          selectedLocalCertificate: {} as CertificateFromLacuna,
        };

        const certificates = LacunaService.filterExpiredCertificates(await LacunaService.listCertificates());

        const userLocalCertificates = certificates.filter((certificate) => {
          return onlyDigits(certificate.pkiBrazil.cpf || '') === get().prescriber()?.Documento;
        });
        const certificateFromLocalStorage = safeGetFromLocalStorage('certificadoLocalSelecionado');

        if (!!certificateFromLocalStorage) {
          const lastUsedCertificate: CertificateFromLacuna = JSON.parse(certificateFromLocalStorage);
          const certificate = certificates.find((c) => c.thumbprint === lastUsedCertificate.thumbprint);
          if (certificate) {
            dataToSave.selectedLocalCertificate = certificate;
          }
        } else if (userLocalCertificates.length >= 1) {
          dataToSave.selectedLocalCertificate = userLocalCertificates[0];
        }

        set((prev) => ({
          ...prev,
          ...dataToSave,
        }));
        return;
      }

      // possui certificado digital
      if (!isEmpty(availableCertificates)) {
        // fluxo caso prescritor possui apenas um certificado digital
        if (availableCertificates.length === 1) {
          const userCertificate = availableCertificates[0] || null;

          set((prev) => {
            return {
              ...prev,
              selectedCertificateType: userCertificate,
              hideManualSigningMethod: userCertificate !== CERTIFICATES.MANUAL,
            };
          });
          return;
        }

        // fluxo caso prescritor possui mais que um certificado digital
        if (get().prescriberConfiguration?.PrimeiroAcesso) {
          set((prev) => {
            return {
              ...prev,
              selectedCertificateType: CERTIFICATES.NOT_DEFINED,
              hideManualSigningMethod: true,
            };
          });
          return;
        }

        if (
          params.lastSignatureMethod === CERTIFICATES.NOT_DEFINED ||
          !params.lastSignatureMethod ||
          params.lastSignatureMethod === CERTIFICATES.MANUAL
        ) {
          set((prev) => {
            return {
              ...prev,
              selectedCertificateType: CERTIFICATES.NOT_DEFINED,
              hideManualSigningMethod: true,
            };
          });
          return;
        }

        set((prev) => {
          return {
            ...prev,
            selectedCertificateType: params.lastSignatureMethod,
            hideManualSigningMethod: true,
          };
        });

        return;
      }

      // fluxo caso prescritor não possui certificado digital
      // ou quando todos os certificados retornados têm status 'N'
      set((prev) => {
        return {
          ...prev,
          selectedCertificateType: CERTIFICATES.MANUAL,
          hideManualSigningMethod: false,
        };
      });
    } catch (error: any) {
      if (error.response) {
        set((prevState) => {
          const prevPrescription = prevState.prescription;
          if (!prevPrescription) return prevState;

          const newPrescription = {
            ...prevPrescription,
            UltimoMetodoAssinatura: CERTIFICATES.MANUAL,
            hideManualSigningMethod: false,
          };

          return {
            ...prevState,
            prescription: newPrescription,
            ownedCertificates: [],
            selectedCertificateType: CERTIFICATES.MANUAL,
            hideManualSigningMethod: false,
          };
        });
      }
    }
  },

  filteredCertificates: () => {
    const hasBypass = get().shouldBypassCertificateFlow;

    const filteredCertificates =
      get().digitalCertificateIsRequired() && !hasBypass
        ? certificates.filter((certificate) => certificate.value !== CERTIFICATES.MANUAL)
        : certificates;

    const userOwnedCertificates = get()
      .ownedCertificates.filter((certificate) => certificate.status === 'S')
      .map((certificate) => certificate.name);

    const availableCertificates = filteredCertificates.filter(
      (certificate) =>
        userOwnedCertificates.includes(certificate.value) ||
        certificate.value === CERTIFICATES.LOCAL ||
        certificate.value === CERTIFICATES.MANUAL
    );

    const result = availableCertificates.reduce<Certificate[]>((acc, item) => {
      const certificate = get().ownedCertificates.find((ownedCertificate) => ownedCertificate.name === item.value);

      if (certificate) {
        return [
          ...acc,
          { ...item, certifierStatus: certificate.certifierStatus, description: certificate.description },
        ];
      }

      return [...acc, item];
    }, []);

    return result;
  },

  prescriberConfigurations: null,
  setPrescriberConfigurations: (data: PrescriberConfigurations | null) => {
    set((prev) => {
      return {
        ...prev,
        prescriberConfigurations: data,
      };
    });
  },

  getAndSetPrescriberConfiguration: async (prescriberId: number) => {
    try {
      const prescriberConfigurations = await PrescriberService.getPrescriberConfigurations(prescriberId);

      set((prev) => {
        return {
          ...prev,
          prescriberConfigurations: prescriberConfigurations,
        };
      });
    } catch (error) {
      toast.error('Falha ao carregar informações do prescritor. Inicie uma nova receita.');
    }
  },

  getPrescriberCoreData: async (params: PrescriberCoreDataParams) => {
    await get().getAndSetPrescriberConfiguration(params.prescriberId);
    const prescriberConfiguration = get().prescriberConfigurations;
    const lastSignatureMethod = (prescriberConfiguration?.UltimaAssinatura as CERTIFICATES) || CERTIFICATES.MANUAL;

    await Promise.all([
      await get().getAndSetPrescriberCertificates({
        prescriberDocument: params.prescriberDocument,
        prescriptionId: params.prescriptionId,
        lastSignatureMethod: lastSignatureMethod === 'LACUNA' ? CERTIFICATES.LOCAL : lastSignatureMethod,
        isDigitalCertificateRequired: params.isDigitalCertificateRequired,
      }),
    ]);
  },

  rqes: [],
  setRqes: (data: RQE[]) => {
    set((prev) => {
      return {
        ...prev,
        rqes: data,
      };
    });
  },

  selectedRqe: null,
  setSelectedRqe: (data: number | null) => {
    set((prev) => {
      return {
        ...prev,
        selectedRqe: data,
      };
    });
  },
  selectedLocalCertificate: null,
  setSelectedLocalCertificate: (data: CertificateFromLacuna | null) => {
    set((prev) => {
      return {
        ...prev,
        selectedLocalCertificate: data,
      };
    });
  },
  searchMedicineId: 0,
  setSearchMedicineId: (data: number) => {
    set((prev) => {
      return {
        ...prev,
        searchMedicineId: data,
      };
    });
  },

  doesPrescriptionHasSigningWarning: () => {
    const prescription = get().prescription;
    const selectedCertificateType = get().selectedCertificateType;

    if (!prescription) return false;

    const prescriptionItemsAmountLessMedicines =
      get().prescriptionItemsAmount() - prescription.Itens.length - prescription.ItensManuais.length;

    const doesPrescriptionHasOnlyDocuments = prescriptionItemsAmountLessMedicines > 0;

    const medicinesThatNeedsDigitalSignature = prescription.Itens.filter(
      (medicine) =>
        MedicineCategorizer.isSpecialControlOrAntimicrobial(medicine) ||
        MedicineCategorizer.isPrescriptionWithNotification(medicine)
    );

    const doesPrescriptionOnlyHasBlueStripe = prescription.Itens.every((medicine) =>
      MedicineCategorizer.isBlueStripe(medicine)
    );

    if (selectedCertificateType !== CERTIFICATES.MANUAL) return false;

    return (
      doesPrescriptionHasOnlyDocuments ||
      !isEmpty(medicinesThatNeedsDigitalSignature) ||
      (doesPrescriptionOnlyHasBlueStripe && doesPrescriptionHasOnlyDocuments)
    );
  },
  initialActiveTab: SEARCH_TYPE.MEDICINE,
  setInitialActiveTab: (data?: ACTIVE_TAB) => {
    set((prev) => {
      const configs = get().prescriberConfiguration;
      const allowedTabs = {
        [ACTIVE_TAB.CERTIFICATE]: LodashGet(configs, 'HabilitarAtestado', false),
        [ACTIVE_TAB.EXAM]: LodashGet(configs, 'HabilitarExame', false),
        [ACTIVE_TAB.REPORT]: LodashGet(configs, 'Features.enableMedicalReport', false),
        [ACTIVE_TAB.REFERRAL]: LodashGet(configs, 'Features.enableReferral', false),
        [ACTIVE_TAB.PRESCRIPTION]: true,
      };

      if (!data || !allowedTabs[data]) {
        return {
          ...prev,
          initialActiveTab: SEARCH_TYPE.MEDICINE,
        };
      }

      const tab: SEARCH_TYPE = ACTIVE_TAB_MAP_TO_SEARCH_TAB[data];
      useSearchTypeStore.getState().setSearchType(tab);
      return {
        ...prev,
        initialActiveTab: tab,
      };
    });
  },
  hideManualSigningMethod: true,
  setHideManualSigningMethod: (value: boolean) => {
    set((prev) => {
      return {
        ...prev,
        hideManualSigningMethod: value,
      };
    });
  },

  emittedTo: new Date(),
  setEmittedTo: (value: Date) => {
    set((prev) => {
      return {
        ...prev,
        emittedTo: value,
      };
    });
  },

  shouldHideCertificates: false,
  setShouldHideCertificates: (value: boolean) => {
    set((prev) => {
      return {
        ...prev,
        shouldHideCertificates: value,
      };
    });
  },
  userCertificates: () => {
    const shouldFilterLocal = !get().prescription?.ExibirCertificadoLocal;
    const isManualSigningHidden = get().hideManualSigningMethod;
    const hasBypass = get().shouldBypassCertificateFlow;

    const filteredCertificates = get()
      .filteredCertificates()
      .filter((certificate: Certificate) => {
        if (hasBypass) {
          return true;
        }
        if (certificate.value === CERTIFICATES.LOCAL && shouldFilterLocal) {
          return false;
        }
        if (certificate.value === CERTIFICATES.MANUAL && isManualSigningHidden) {
          return false;
        }
        return true;
      });

    // Há ao menos uma certificadora disponível para assinar? (o médico possui o certificado
    // E a certificadora não está instável/indisponível). Certificadora saudável retorna
    // certifierStatus "N/A", por isso usamos "não instável" em vez de "== ACTIVE".
    const hasAvailableCertifier = get().ownedCertificates.some(
      (certificate) => certificate.status === 'S' && !isCertifierUnstable(certificate.certifierStatus)
    );

    // Com certificado digital obrigatório, oferece "Assinar e Carimbar" como saída quando não há
    // nenhuma certificadora disponível: lista vazia, médico sem certificado, ou todas as suas
    // certificadoras instáveis/indisponíveis (PARTIALLY_ACTIVE/INACTIVE).
    if (get().digitalCertificateIsRequired()) {
      if (filteredCertificates.length === 0 || !hasAvailableCertifier) {
        // Verifica se o manual já não está na lista para evitar duplicatas
        const hasManualCertificate = filteredCertificates.some((cert) => cert.value === CERTIFICATES.MANUAL);

        if (!hasManualCertificate) {
          filteredCertificates.push({
            name: 'Assinar </br> e carimbar',
            imagePath: manualLogo,
            value: CERTIFICATES.MANUAL,
            certifierStatus: CERTIFIER_STATUS.NOT_APPLICABLE,
            description: null,
          });
        }
      }
    }

    return filteredCertificates;
  },
  fetchFavoriteExams: async () => {
    const prescription = get().prescription;
    if (!prescription) return;

    try {
      const data = await ExamService.getFavoriteExams(prescription.Prescritor.idPrescritor, prescription.idPrescricao);

      set((prev) => {
        return {
          ...prev,
          favoriteExams: data,
        };
      });

      return;
    } catch (e: any) {
      toast.error('Não foi possível buscar os exames favoritos, tente novamente');
    }
  },
  addFavoriteExam: async (exam: Exam) => {
    const prescription = get().prescription;
    if (!prescription || !exam.referencia) return;

    try {
      await ExamService.addFavoriteExam(prescription.Prescritor.idPrescritor, exam.examId, exam.customExamIndex);
      await get().fetchFavoriteExams();
    } catch (e: any) {
      if (e?.response?.status === 400 && e?.response?.data?.message) {
        toast.error(e.response.data.message);
      } else {
        toast.error('Não foi possível favoritar o exame, tente novamente');
      }
    }
  },

  removeFavoriteExam: async (exam: Exam) => {
    const prescription = get().prescription;
    if (!prescription || !exam.referencia) return;

    try {
      await ExamService.removeFavoriteExam(prescription.Prescritor.idPrescritor, exam.examId);
      await get().fetchFavoriteExams();
    } catch (e: any) {
      toast.error('Não foi possível remover favorito do exame, tente novamente');
    }
  },

  favoriteExams: [],
  setFavoriteExams: (favoriteExams: FavoriteExam[]) => {
    if (!favoriteExams) return;
    set((prev) => {
      return {
        ...prev,
        favoriteExams,
      };
    });
  },
}));
